import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '@/contexts/AuthContext';
import { useTheme } from '@/contexts/ThemeContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import ProfileAvatarImage from '@/components/ProfileAvatarImage';
import { Separator } from '@/components/ui/separator';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Sun, Moon, Monitor, Loader2, Save, User, Palette, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import { motion } from 'framer-motion';
import { API_URL } from '@/lib/api';

export default function SettingsPage() {
  const { user, updateUser } = useAuth();
  const { theme, setTheme } = useTheme();
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    bio: user?.bio || '',
    profile_picture: user?.profile_picture || ''
  });
  const [saving, setSaving] = useState(false);

  const handleChange = (e) => {
    setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSaveProfile = async (e) => {
    e.preventDefault();
    setSaving(true);
    
    try {
      const response = await axios.put(
        `${API_URL}/api/users/me`,
        formData,
        
      );
      updateUser(response.data);
      toast.success('Profile updated successfully!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update profile');
    } finally {
      setSaving(false);
    }
  };

  const handleThemeChange = async (newTheme) => {
    setTheme(newTheme);
    
    try {
      await axios.put(
        `${API_URL}/api/users/me`,
        { theme_preference: newTheme },
        
      );
    } catch (error) {
      console.error('Failed to save theme preference:', error);
    }
  };

  // Account deletion state
  const [deleteStep, setDeleteStep] = useState(0); // 0=closed, 1=confirm, 2=comments, 3=final
  const [deleteComments, setDeleteComments] = useState(null);
  const [confirmName, setConfirmName] = useState('');
  const [deleting, setDeleting] = useState(false);

  const handleDeleteAccount = async () => {
    setDeleting(true);
    try {
      await axios.delete(`${API_URL}/api/auth/account`, {
        data: { delete_comments: deleteComments, confirm_name: confirmName }
      });
      toast.success('Your account has been permanently deleted.');
      setDeleteStep(0);
      // Force reload to clear auth state
      window.location.href = '/';
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete account');
    } finally {
      setDeleting(false);
    }
  };

  const resetDeleteFlow = () => {
    setDeleteStep(0);
    setDeleteComments(null);
    setConfirmName('');
  };

  const nameMatches = confirmName === (user?.display_name || '');

  return (
    <div className="max-w-2xl mx-auto px-4 py-8" data-testid="settings-page">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
      >
        <h1 className="font-serif text-3xl font-bold mb-2">Settings</h1>
        <p className="text-muted-foreground mb-8">Manage your account and preferences</p>

        {/* Profile Settings */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <User className="w-5 h-5" />
              Profile
            </CardTitle>
            <CardDescription>
              Update your public profile information
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSaveProfile} className="space-y-6">
              {/* Avatar Preview */}
              <div className="flex items-center gap-4">
                <Avatar className="w-20 h-20">
                  <ProfileAvatarImage
                    src={formData.profile_picture}
                    alt={user?.display_name || ''}
                    zoom={user?.profile_picture_zoom}
                    positionX={user?.profile_picture_position_x}
                    positionY={user?.profile_picture_position_y}
                  />
                  <AvatarFallback className="text-2xl">{(user?.display_name || user?.username)?.[0]?.toUpperCase()}</AvatarFallback>
                </Avatar>
                <div className="flex-1">
                  <Label htmlFor="profile_picture">Profile Picture URL</Label>
                  <Input
                    id="profile_picture"
                    name="profile_picture"
                    value={formData.profile_picture}
                    onChange={handleChange}
                    placeholder="https://example.com/avatar.jpg"
                    className="mt-1"
                    data-testid="profile-picture-input"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="bio">Bio</Label>
                <Textarea
                  id="bio"
                  name="bio"
                  value={formData.bio}
                  onChange={handleChange}
                  placeholder="Tell us about yourself..."
                  className="min-h-[100px]"
                  data-testid="bio-input"
                />
              </div>

              <Button 
                type="submit" 
                className="btn-primary"
                disabled={saving}
                data-testid="save-profile-btn"
              >
                {saving ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Save className="w-4 h-4 mr-2" />
                )}
                Save Changes
              </Button>
            </form>
          </CardContent>
        </Card>

        {/* Theme Settings */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Palette className="w-5 h-5" />
              Appearance
            </CardTitle>
            <CardDescription>
              Customize how Secrets Club looks on your device
            </CardDescription>
          </CardHeader>
          <CardContent>
            <RadioGroup value={theme} onValueChange={handleThemeChange} className="space-y-4">
              <div className="flex items-center space-x-4 p-4 border border-border rounded-xl hover:bg-muted/50 cursor-pointer" data-testid="theme-light-option">
                <RadioGroupItem value="light" id="light" />
                <Label htmlFor="light" className="flex items-center gap-3 cursor-pointer flex-1">
                  <div className="w-10 h-10 rounded-full bg-paper flex items-center justify-center border">
                    <Sun className="w-5 h-5 text-forest-700" />
                  </div>
                  <div>
                    <p className="font-medium">Light</p>
                    <p className="text-sm text-muted-foreground">A clean, bright appearance</p>
                  </div>
                </Label>
              </div>

              <div className="flex items-center space-x-4 p-4 border border-border rounded-xl hover:bg-muted/50 cursor-pointer" data-testid="theme-dark-option">
                <RadioGroupItem value="dark" id="dark" />
                <Label htmlFor="dark" className="flex items-center gap-3 cursor-pointer flex-1">
                  <div className="w-10 h-10 rounded-full bg-void flex items-center justify-center border border-white/10">
                    <Moon className="w-5 h-5 text-forest-400" />
                  </div>
                  <div>
                    <p className="font-medium">Dark</p>
                    <p className="text-sm text-muted-foreground">Easy on the eyes in low light</p>
                  </div>
                </Label>
              </div>

              <div className="flex items-center space-x-4 p-4 border border-border rounded-xl hover:bg-muted/50 cursor-pointer" data-testid="theme-auto-option">
                <RadioGroupItem value="auto" id="auto" />
                <Label htmlFor="auto" className="flex items-center gap-3 cursor-pointer flex-1">
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-paper to-void flex items-center justify-center border">
                    <Monitor className="w-5 h-5 text-muted-foreground" />
                  </div>
                  <div>
                    <p className="font-medium">System</p>
                    <p className="text-sm text-muted-foreground">Match your device settings</p>
                  </div>
                </Label>
              </div>
            </RadioGroup>
          </CardContent>
        </Card>

        {/* Account Info */}
        <Card className="mt-6">
          <CardHeader>
            <CardTitle>Account Information</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex justify-between items-center py-2">
              <span className="text-muted-foreground">Email</span>
              <span className="font-medium">{user?.email}</span>
            </div>
            <Separator />
            <div className="flex justify-between items-center py-2">
              <span className="text-muted-foreground">Role</span>
              <span className="font-medium capitalize">{user?.role}</span>
            </div>
            <Separator />
            <div className="flex justify-between items-center py-2">
              <span className="text-muted-foreground">Account Type</span>
              <span className="font-medium">{user?.premium_tier > 0 ? 'Premium' : 'Free'}</span>
            </div>
          </CardContent>
        </Card>

        {/* Delete Account */}
        <div className="mt-8 mb-4 text-center">
          <button
            onClick={() => setDeleteStep(1)}
            className="text-sm text-red-500 hover:text-red-600 hover:underline transition-colors"
            data-testid="delete-account-link"
          >
            Delete my account
          </button>
        </div>

        {/* Step 1: Are you sure? */}
        <Dialog open={deleteStep === 1} onOpenChange={(open) => { if (!open) resetDeleteFlow(); }}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-red-600">
                <AlertTriangle className="w-5 h-5" />
                Delete Your Account?
              </DialogTitle>
              <DialogDescription>
                Are you sure you want to delete your account? All your data and information will be <strong>permanently deleted and unrecoverable</strong>. We do not save client data.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter className="gap-2 sm:gap-0">
              <Button variant="outline" onClick={resetDeleteFlow} data-testid="delete-step1-cancel">Cancel</Button>
              <Button variant="destructive" onClick={() => setDeleteStep(2)} data-testid="delete-step1-confirm">Confirm</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Step 2: Delete comments? */}
        <Dialog open={deleteStep === 2} onOpenChange={(open) => { if (!open) resetDeleteFlow(); }}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-red-600">
                <AlertTriangle className="w-5 h-5" />
                What About Your Comments?
              </DialogTitle>
              <DialogDescription>
                Do you also want to delete all your comments? If you choose not to delete your comments, your name will be changed to <strong>"A former member"</strong> on those comments.
              </DialogDescription>
            </DialogHeader>
            <div className="flex flex-col gap-2 py-2">
              <Button
                variant="outline"
                className="w-full justify-start"
                onClick={() => { setDeleteComments(false); setDeleteStep(3); }}
                data-testid="delete-step2-leave"
              >
                Leave my Comments
              </Button>
              <Button
                variant="destructive"
                className="w-full justify-start"
                onClick={() => { setDeleteComments(true); setDeleteStep(3); }}
                data-testid="delete-step2-delete"
              >
                Delete my Comments
              </Button>
              <Button
                variant="ghost"
                className="w-full justify-start text-muted-foreground"
                onClick={resetDeleteFlow}
                data-testid="delete-step2-cancel"
              >
                Cancel
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Step 3: Final confirmation with display name */}
        <Dialog open={deleteStep === 3} onOpenChange={(open) => { if (!open) resetDeleteFlow(); }}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-red-600">
                <AlertTriangle className="w-5 h-5" />
                Last Chance
              </DialogTitle>
              <DialogDescription>
                If you delete your account it can never be reinstated and you must create a new account. Type your display name in the text bar below to delete your account.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-3 py-2">
              <p className="text-sm text-center">
                Your display name: <strong className="text-foreground">{user?.display_name}</strong>
              </p>
              <Input
                placeholder="Type your display name here"
                value={confirmName}
                onChange={(e) => setConfirmName(e.target.value)}
                data-testid="delete-confirm-name-input"
              />
            </div>
            <DialogFooter className="gap-2 sm:gap-0">
              <Button variant="outline" onClick={resetDeleteFlow} data-testid="delete-step3-cancel">Cancel</Button>
              <Button
                disabled={!nameMatches || deleting}
                onClick={handleDeleteAccount}
                className={`transition-colors ${
                  nameMatches
                    ? 'bg-forest-700 hover:bg-forest-800 text-white'
                    : 'bg-forest-900/40 text-white/50 cursor-not-allowed'
                }`}
                data-testid="delete-step3-confirm"
              >
                {deleting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                Permanently Delete My Account
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </motion.div>
    </div>
  );
}
